import { useTranslation } from 'react-i18next'
import { alpha, Box, Grid, Stack, Tooltip, Typography, useTheme } from '@mui/material'
import { useNavigate } from 'react-router-dom'
import LaunchRoundedIcon from '@mui/icons-material/LaunchRounded'
import { ContextModelSummary, ModuleSummary } from '../../shared/model/models'
import { WALKTHROUGH_STATUS, diversityDimensionMap } from '../../shared/constants'
import { ModuleConfig } from '../../shared/model'
import { useAppDispatch, useAppSelector } from '../../store/hooks'
import { changeWalkthroughStatus } from '../../store/actions'
import { translatedDomainName } from '../../shared/utils'
import AnimatedProgressBar from '../common/molecules/AnimatedProgressBar'
import { getIcon } from '../common/Icons'

type FsModuleSummaryProps = {
  moduleSummary: ModuleSummary
  moduleConfig?: ModuleConfig
  detailedView: boolean
  noOfModules: number
}

const FsModuleSummary = ({
  moduleSummary,
  moduleConfig,
  detailedView,
  noOfModules,
}: FsModuleSummaryProps) => {
  const { t } = useTranslation()
  const theme = useTheme()
  const dispatch = useAppDispatch()
  const { walkthroughStatus, currentLanguage } = useAppSelector((state) => state.currentUser)

  const navigate = useNavigate()

  const sortedItems = [...moduleSummary.contextModelSummaries].sort((a, b) =>
    a.diversityDimensionCharacteristic.empiricalConcept.order <
    b.diversityDimensionCharacteristic.empiricalConcept.order
      ? -1
      : 1,
  )

  return (
    <Grid alignItems="baseline" alignContent="center" className="mt-7">
      <Grid
        container
        alignItems="flex-start"
        alignContent="center"
        className={`${moduleConfig?.module}icongraph walkthroughdivclass`}
      >
        <Typography variant="h3" className="w-full mb-2">
          {t('overview.bar.chart.heading')}
        </Typography>
        <Grid container>
          {sortedItems.map((cms, index) => (
            <Grid size={noOfModules === 1 ? 6 : 12}>
              <Stack className="items-center justify-center w-full">
                {cms.contextModelVariants.length > 1 ? (
                  <Tooltip
                    placement="right"
                    title={
                      <Typography component="div">
                        <Typography variant="caption" sx={{ verticalAlign: 'super' }}>
                          {'\u2731'}
                        </Typography>{' '}
                        {`${
                          currentLanguage === 'de'
                            ? 'Wenn eine Diversitätsdimension aus mehreren Vergleichsgruppen besteht, wird der Durchschnitt angezeigt.'
                            : 'If a diversity dimension consists of several comparison groups, the average is displayed.'
                        }`}
                      </Typography>
                    }
                  >
                    <Stack
                      key={`mod_${moduleConfig?.module}_divdim_${index}`}
                      className="items-center justify-center w-full py-4 cursor-pointer rounded"
                      sx={{
                        '&:hover': {
                          backgroundColor: theme.palette.action.hover,
                          borderRadius: 2,
                        },
                      }}
                      onClick={() => {
                        if (walkthroughStatus === WALKTHROUGH_STATUS.InProgress) {
                          dispatch(changeWalkthroughStatus(WALKTHROUGH_STATUS.Paused))
                        }
                        navigate(
                          `/fs/fair-${moduleConfig?.module}/${cms.contextModelVariants[0].id}`,
                        )
                      }}
                    >
                      <Stack
                        className="flex-row justify-between items-center"
                        width={{ lg: '95%', md: '60%' }}
                        minWidth={200}
                      >
                        <Stack className="flex-row items-center">
                          <Box width={24} height={24}>
                            {getIcon(cms.diversityDimensionCharacteristic.name, {
                              color:
                                theme.palette.mode === 'light'
                                  ? diversityDimensionMap.get(
                                      cms.diversityDimensionCharacteristic.name,
                                    )?.fsColor?.primary ?? ''
                                  : diversityDimensionMap.get(
                                      cms.diversityDimensionCharacteristic.name,
                                    )?.fsColor?.light ?? '',
                            })}
                          </Box>
                          <Typography className="ml-2" width="85%">
                            {t(
                              `context.model.name.${cms.diversityDimensionCharacteristic.name}` as any,
                            )}
                          </Typography>
                        </Stack>
                        <Typography className="text-right" component="div">
                          {`${Number(cms.fairnessAchieved).toLocaleString(currentLanguage, {
                            maximumFractionDigits: 1,
                          })} % `}
                          <Typography
                            variant="caption"
                            color="textDisabled"
                            sx={{ verticalAlign: 'super' }}
                          >
                            {`${cms.contextModelVariants.length > 1 ? '\u2731' : ''}`}
                          </Typography>
                        </Typography>
                      </Stack>
                      <Grid
                        size={{ xs: 12 }}
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        borderRadius={2}
                        mx="auto"
                        mt={1}
                        width="60%"
                        minWidth={200}
                      >
                        <AnimatedProgressBar
                          progress={cms.fairnessAchieved > 0 ? cms.fairnessAchieved : 0}
                          color={
                            diversityDimensionMap.get(cms.diversityDimensionCharacteristic.name)
                              ?.fsColor?.light ?? ''
                          }
                        />
                      </Grid>
                    </Stack>
                  </Tooltip>
                ) : (
                  <Stack
                    key={`mod_${moduleConfig?.module}_divdim_${index}`}
                    className="items-center justify-center w-full py-4 cursor-pointer rounded"
                    sx={{
                      '&:hover': {
                        backgroundColor: theme.palette.action.hover,
                        borderRadius: 2,
                      },
                    }}
                    onClick={() => {
                      if (walkthroughStatus === WALKTHROUGH_STATUS.InProgress) {
                        dispatch(changeWalkthroughStatus(WALKTHROUGH_STATUS.Paused))
                      }
                      navigate(`/fs/fair-${moduleConfig?.module}/${cms.contextModelVariants[0].id}`)
                    }}
                  >
                    <Stack
                      className="flex-row justify-between items-center"
                      width={{ lg: '95%', md: '60%' }}
                      minWidth={200}
                    >
                      <Stack className="flex-row items-center">
                        <Box width={24} height={24}>
                          {getIcon(cms.diversityDimensionCharacteristic.name, {
                            color:
                              theme.palette.mode === 'light'
                                ? diversityDimensionMap.get(
                                    cms.diversityDimensionCharacteristic.name,
                                  )?.fsColor?.primary ?? ''
                                : diversityDimensionMap.get(
                                    cms.diversityDimensionCharacteristic.name,
                                  )?.fsColor?.light ?? '',
                          })}
                        </Box>
                        <Typography className="ml-2" width="85%">
                          {t(
                            `context.model.name.${cms.diversityDimensionCharacteristic.name}` as any,
                          )}
                        </Typography>
                      </Stack>
                      <Typography className="text-right" component="div">
                        {`${Number(cms.fairnessAchieved).toLocaleString(currentLanguage, {
                          maximumFractionDigits: 1,
                        })} % `}
                        <Typography variant="caption" sx={{ verticalAlign: 'super' }}>
                          {`${cms.contextModelVariants.length > 1 ? '\u2731' : ''}`}
                        </Typography>
                      </Typography>
                    </Stack>
                    <Grid
                      size={{ xs: 12 }}
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      borderRadius={2}
                      mx="auto"
                      mt={1}
                      width="60%"
                      minWidth={200}
                    >
                      <AnimatedProgressBar
                        progress={cms.fairnessAchieved > 0 ? cms.fairnessAchieved : 0}
                        color={
                          diversityDimensionMap.get(cms.diversityDimensionCharacteristic.name)
                            ?.fsColor?.light ?? ''
                        }
                      />
                    </Grid>
                  </Stack>
                )}

                {(detailedView || noOfModules === 1) && (
                  <Stack className="w-full mb-4">
                    <Box mx={2}>
                      {cms.contextModelVariants.map((cItem) => (
                        <Stack
                          direction="row"
                          mt={2}
                          p={0.5}
                          px={2}
                          borderRadius={1}
                          className="flex items-start justify-between w-full cursor-pointer"
                          sx={{
                            boxShadow: `0px 0px 4px 0px ${alpha(
                              diversityDimensionMap.get(cms.diversityDimensionCharacteristic.name)
                                ?.fsColor?.light ?? '',
                              0.25,
                            )}`,
                            '&:hover': {
                              backgroundColor: alpha(
                                diversityDimensionMap.get(cms.diversityDimensionCharacteristic.name)
                                  ?.fsColor?.light ?? '',
                                0.05,
                              ),
                            },
                          }}
                          onClick={() => {
                            if (walkthroughStatus === WALKTHROUGH_STATUS.InProgress) {
                              dispatch(changeWalkthroughStatus(WALKTHROUGH_STATUS.Paused))
                            }
                            navigate(`/fs/fair-${moduleConfig?.module}/${cItem.id}`)
                          }}
                        >
                          <Typography className="mt-1 mb-1 mr-4">
                            {`${translatedDomainName(
                              cItem.diversityVariant.groupOfInterestVariable.label,
                              t,
                            )} ${t('vs.')} ${translatedDomainName(
                              cItem.diversityVariant.referenceGroupVariable.label,
                              t,
                            )}`}
                          </Typography>
                          <LaunchRoundedIcon
                            sx={{
                              fontSize: '14px',
                              mt: 1.25,
                              color: theme.palette.text.secondary,
                            }}
                          />
                        </Stack>
                      ))}
                    </Box>
                  </Stack>
                )}
              </Stack>
            </Grid>
          ))}
        </Grid>
      </Grid>
    </Grid>
  )
}

export default FsModuleSummary
